try {
localStorage.removeItem('ai-public_search_token');
localStorage.removeItem('ai-public_search_token_expiry');
} catch (e) {}

(function (window) {
    'use strict';

    // Single global guard - if already defined, bail (no double-init)
    if (window.GoddivaAiAuth) return;

    /* -------------------- Configuration -------------------- */
    var AUTH_URL = window.GD_CONFIG.aiApiBase + '/personalization/auth/client-token';
    var TOKEN_KEY = 'ai-public_search_morelikethis_token';
    var EXPIRY_KEY = 'ai-public_search_morelikethis_token_expiry';
    var REFRESH_BUFFER_MS = 60 * 1000; // refresh 60s before actual expiry

    /* -------------------- Storage helpers -------------------- */
    function readToken() {
        try {
            var token = localStorage.getItem(TOKEN_KEY);
            var expiry = parseInt(localStorage.getItem(EXPIRY_KEY), 10);
            if (!token || !expiry) return null;
            if (Date.now() >= expiry - REFRESH_BUFFER_MS) return null;
            return token;
        } catch (e) {
            return null;
        }
    }

    function writeToken(token, expiresInSeconds) {
        try {
            localStorage.setItem(TOKEN_KEY, token);
            localStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresInSeconds * 1000));
        } catch (e) {
            console.warn('[GoddivaAiAuth] localStorage write failed', e);
        }
    }

    function removeToken() {
        try {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(EXPIRY_KEY);
        } catch (e) {
            // best-effort
        }
    }

    /* -------------------- Inflight dedup -------------------- */
    // When multiple consumers ask for a token before the auth call
    // completes, share the same Promise rather than triggering N calls.
    var inflightFetch = null;

    function fetchFreshToken() {
        if (inflightFetch) return inflightFetch;

        inflightFetch = fetch(AUTH_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
        .then(function (res) {
            if (!res.ok) throw new Error('Auth failed: ' + res.status);
            return res.json();
        })
        .then(function (data) {
            if (!data || !data.access_token) {
                throw new Error('No access_token in auth response');
            }
            var expiresIn = parseInt(data.expires_in, 10) || 3600;
            writeToken(data.access_token, expiresIn);
            return data.access_token;
        })
        .catch(function (err) {
            console.error('[GoddivaAiAuth] fetchFreshToken failed', err);
            throw err;
        })
        .finally(function () {
            inflightFetch = null;
        });

        return inflightFetch;
    }

    /* -------------------- Public API: getValidToken -------------------- */
    /**
     * Returns a Promise resolving to a valid Bearer token.
     * Reuses cached token if not expired (with 60s buffer),
     * otherwise fetches fresh.
     */
    function getValidToken() {
        var existing = readToken();
        if (existing) return Promise.resolve(existing);
        return fetchFreshToken();
    }

    /* -------------------- Public API: clearToken -------------------- */
    function clearToken() {
        removeToken();
    }

    /* -------------------- Public API: authFetch (fetch-based) -------------------- */
    /**
     * Drop-in fetch() replacement that auto-adds Authorization header
     * and retries once on 401 with a fresh token.
     *
     * Usage:
     *   window.GoddivaAiAuth.authFetch('https://ai.../some-endpoint', {
     *     method: 'POST',
     *     body: JSON.stringify(payload),
     *   }).then(r => r.json()).then(...)
     */
    function authFetch(url, options, retry) {
        if (retry === undefined) retry = true;
        options = options || {};

        return getValidToken().then(function (token) {
            var headers = Object.assign({}, options.headers || {});
            headers['Authorization'] = 'Bearer ' + token;

            // Only auto-set Content-Type if no body or non-FormData body
            if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
                headers['Content-Type'] = 'application/json';
            }

            return fetch(url, {
                method: options.method || 'GET',
                headers: headers,
                body: options.body || null,
                signal: options.signal
            }).then(function (res) {
                if (res.status === 401 && retry) {
                    clearToken();
                    return authFetch(url, options, false);
                }
                return res;
            });
        });
    }

    /* -------------------- Public API: authAjax (jQuery-based) -------------------- */
    /**
     * Drop-in $.ajax replacement for legacy jQuery consumers.
     * Returns a jQuery promise. Auto-adds Authorization header,
     * retries once on 401.
     *
     * Usage:
     *   window.GoddivaAiAuth.authAjax({
     *     url: 'https://ai.../endpoint',
     *     method: 'GET'
     *   }).done(function(data) { ... });
     */
    function authAjax(ajaxOptions, retry) {
        if (retry === undefined) retry = true;
        ajaxOptions = ajaxOptions || {};

        if (typeof jQuery === 'undefined') {
            return Promise.reject(new Error('jQuery not available'));
        }
        var $ = jQuery;
        var deferred = $.Deferred();

        getValidToken().then(function (token) {
            var headers = $.extend({}, ajaxOptions.headers || {});
            headers['Authorization'] = 'Bearer ' + token;
            var requestOptions = $.extend({}, ajaxOptions, { headers: headers });

            $.ajax(requestOptions)
                .done(function (data, textStatus, xhr) {
                    deferred.resolve(data, textStatus, xhr);
                })
                .fail(function (xhr, textStatus, errorThrown) {
                    if (xhr && xhr.status === 401 && retry) {
                        clearToken();
                        authAjax(ajaxOptions, false)
                            .done(function (data, ts, x) { deferred.resolve(data, ts, x); })
                            .fail(function (x, ts, err) { deferred.reject(x, ts, err); });
                        return;
                    }
                    deferred.reject(xhr, textStatus, errorThrown);
                });
        }, function (err) {
            deferred.reject(err);
        });

        return deferred.promise();
    }

    /* -------------------- Expose to window -------------------- */
    window.GoddivaAiAuth = {
        getValidToken: getValidToken,
        clearToken: clearToken,
        authFetch: authFetch,
        authAjax: authAjax,

        // Constants exposed for advanced use cases
        TOKEN_KEY: TOKEN_KEY,
        EXPIRY_KEY: EXPIRY_KEY
    };

    /* -------------------- Optional: prefetch on load -------------------- */
    // Warms the cache so first interaction is fast. Failures are silent
    // (the next real consumer call will retry).
    getValidToken().catch(function () { /* silent */ });

})(window);